import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock, ArrowLeft, Check, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-12rem)] flex items-center bg-bone">
        <div className="container-jamm max-w-md mx-auto py-16">
          <div className="card-soft text-center">
            <h2 className="font-display text-2xl text-ink">Lien invalide</h2>
            <p className="text-ink-soft mt-2 text-sm">Le lien de réinitialisation est manquant ou incomplet. Demandez un nouveau lien.</p>
            <Link to="/mot-de-passe-oublie" className="btn-primary mt-5 inline-flex" data-testid="reset-restart">Recommencer</Link>
          </div>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Mot de passe : 6 caractères minimum");
      return;
    }
    if (password !== confirm) {
      toast.error("Les deux mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Mot de passe mis à jour. Connectez-vous avec le nouveau.");
      navigate("/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Lien expiré ou invalide. Recommencez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-12rem)] flex items-center bg-bone">
      <div className="container-jamm max-w-md mx-auto py-16">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink mb-4" data-testid="reset-back-login">
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>
        <form onSubmit={submit} className="card-soft" data-testid="reset-form">
          <h2 className="font-display text-3xl text-ink">Nouveau mot de passe</h2>
          <p className="text-ink-soft mt-2 text-sm">Choisissez un mot de passe d'au moins 6 caractères.</p>

          <label className="block mt-6 text-sm text-ink-soft">Nouveau mot de passe</label>
          <div className="mt-1.5 flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
            <Lock className="w-4 h-4 text-ink-mute" />
            <input
              required
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="flex-1 px-3 py-3 bg-transparent focus:outline-none"
              placeholder="••••••••"
              autoComplete="new-password"
              data-testid="reset-password"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="p-2 -mr-2 text-ink-mute hover:text-ink-soft transition rounded-lg"
              aria-label={show ? "Masquer" : "Afficher"}
              tabIndex={-1}
              data-testid="reset-toggle-password"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <label className="block mt-4 text-sm text-ink-soft">Confirmer</label>
          <div className="mt-1.5 flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
            <Lock className="w-4 h-4 text-ink-mute" />
            <input
              required
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="flex-1 px-3 py-3 bg-transparent focus:outline-none"
              placeholder="••••••••"
              autoComplete="new-password"
              data-testid="reset-confirm"
            />
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-6" data-testid="reset-submit">
            {loading ? "Mise à jour…" : (<><Check className="w-4 h-4" /> Mettre à jour</>)}
          </button>
        </form>
      </div>
    </div>
  );
}
